import { useState } from "react";
import { motion } from "framer-motion";
import { Layers, Zap } from "lucide-react";
import { IMAGES } from "@/content";

/**
 * Vorschau: ANIME/MANGA-EFFEKTE im aktuellen Dark-Design.
 * Jede Demo ist nummeriert; der User wählt aus welche umgesetzt werden.
 * Route: /preview-effects
 */

const FX_CSS = `
/* 1. Manga SFX pop */
@keyframes fxPop {
  0% { transform: rotate(-8deg) scale(0.6); opacity: 0; }
  20% { transform: rotate(-8deg) scale(1.15); opacity: 1; }
  35% { transform: rotate(-8deg) scale(0.98); }
  50%,100% { transform: rotate(-8deg) scale(1); opacity: 1; }
}
.fx-sfx { font-family: 'Bangers', cursive; animation: fxPop 1.4s ease-out infinite; }

/* 2. Speedlines burst */
@keyframes fxSpin { to { transform: rotate(360deg); } }
.fx-speedlines {
  background: repeating-conic-gradient(from 0deg at 50% 50%,
    hsl(22 100% 55% / 0.18) 0deg 0.6deg, transparent 0.6deg 5deg);
  animation: fxSpin 18s linear infinite;
  -webkit-mask-image: radial-gradient(circle at 50% 50%, transparent 24%, black 60%);
  mask-image: radial-gradient(circle at 50% 50%, transparent 24%, black 60%);
}

/* 3. Holo foil sweep */
@keyframes fxHolo { 0% { background-position: -150% 0; } 100% { background-position: 150% 0; } }
.fx-holo::after {
  content:''; position:absolute; inset:0; pointer-events:none; mix-blend-mode: color-dodge;
  background: linear-gradient(115deg, transparent 25%, hsl(22 100% 60% / .55) 42%,
    hsl(190 100% 60% / .55) 50%, hsl(320 100% 65% / .55) 58%, transparent 75%);
  background-size: 250% 100%; animation: fxHolo 3.5s linear infinite;
}

/* 4. Glitch heading */
@keyframes fxGlitch {
  0%,92%,100% { text-shadow: 0 0 18px hsl(22 100% 55% / .6); transform: translate(0); }
  93% { text-shadow: 2px 0 hsl(4 90% 60%), -2px 0 hsl(190 100% 55%); transform: translate(-2px,1px); }
  95% { text-shadow: -2px 0 hsl(4 90% 60%), 2px 0 hsl(190 100% 55%); transform: translate(2px,-1px); }
  97% { text-shadow: 0 0 18px hsl(22 100% 55% / .6); transform: translate(0); }
}
.fx-glitch { animation: fxGlitch 4s steps(1) infinite; }

/* 5. Kanji rain */
@keyframes fxFall { 0% { transform: translateY(-120%); opacity: 0; } 10%{opacity:.5;} 90%{opacity:.5;} 100% { transform: translateY(520%); opacity: 0; } }
.fx-kanji { position:absolute; font-family:'Noto Sans JP',sans-serif; font-weight:900;
  color: hsl(22 100% 60% / .5); text-shadow: 0 0 12px hsl(22 100% 50% / .6);
  animation: fxFall linear infinite; }

/* 6. Halftone fade panel */
.fx-halftone { background-image: radial-gradient(hsl(22 90% 60% / .25) 1.4px, transparent 1.4px);
  background-size: 12px 12px; -webkit-mask-image: linear-gradient(135deg, black, transparent 70%);
  mask-image: linear-gradient(135deg, black, transparent 70%); }
`;

const Block = ({ n, title, sub, children }) => (
  <div className="border-b border-border">
    <div className="flex items-center gap-4 bg-card/60 px-6 py-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-black text-primary-foreground">{n}</span>
      <div>
        <p className="font-mono-ui text-xs font-bold uppercase tracking-widest text-foreground">{title}</p>
        <p className="text-[11px] text-muted-foreground">{sub}</p>
      </div>
    </div>
    <div className="relative overflow-hidden px-6 py-14">{children}</div>
  </div>
);

export default function EffectsPreview() {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  return (
    <div data-testid="effects-preview-page" className="min-h-screen bg-background">
      <style>{FX_CSS}</style>

      <div className="px-6 py-8 text-center">
        <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-primary">Anime / Manga Effekt-Demos</p>
        <h1 className="mt-2 font-display text-3xl uppercase tracking-wide text-foreground">Mehr Effekte im aktuellen Design</h1>
        <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">
          6 Effekt-Bausteine im bestehenden Dark/Neon-Look. Sag mir welche
          Nummern dir gefallen – die baue ich dann in die echte Seite ein.
        </p>
      </div>

      {/* 1 SFX */}
      <Block n={1} title="Manga SFX-Text" sub="Comic-Soundwords poppen rein – ドン! バン! ズドン!">
        <div className="flex flex-wrap items-center gap-12 py-6">
          <span className="fx-sfx text-6xl text-accent" style={{ WebkitTextStroke: "3px #000" }}>ドン!</span>
          <span className="fx-sfx text-6xl text-primary" style={{ WebkitTextStroke: "3px #000", animationDelay: ".4s" }}>バン!</span>
          <span className="fx-sfx text-6xl text-foreground" style={{ WebkitTextStroke: "3px #000", animationDelay: ".8s" }}>ズドン!</span>
        </div>
      </Block>

      {/* 2 Speedlines */}
      <Block n={2} title="Action Speedlines" sub="Rotierende Speedlines hinter Headlines / Logo = Impact-Frame">
        <div className="relative flex h-56 items-center justify-center">
          <div aria-hidden className="fx-speedlines absolute inset-0" />
          <h2 className="relative font-display text-5xl uppercase text-foreground">
            Trade <span className="neon-text">Day</span>
          </h2>
        </div>
      </Block>

      {/* 3 Holo card (mouse tilt) */}
      <Block n={3} title="Holografische TCG-Karte" sub="Iridescenter Foil-Glanz + 3D-Tilt bei Mausbewegung">
        <div className="flex justify-center py-4" style={{ perspective: 900 }}>
          <motion.div
            onMouseMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setTilt({ x: ((e.clientY - r.top) / r.height - 0.5) * -16, y: ((e.clientX - r.left) / r.width - 0.5) * 16 });
            }}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            style={{ rotateX: tilt.x, rotateY: tilt.y }}
            className="fx-holo relative aspect-[3/4] w-52 overflow-hidden rounded-2xl border-2 border-primary/60"
          >
            <img src={IMAGES.tcgCards} alt="" className="h-full w-full object-cover" />
            <span className="absolute bottom-3 right-3 z-10 font-jp text-2xl font-black text-white" style={{ textShadow: "0 0 10px hsl(22 100% 55%)" }}>渋谷</span>
          </motion.div>
        </div>
      </Block>

      {/* 4 Glitch heading */}
      <Block n={4} title="Glitch-Neon-Headline" sub="Chromatic-Aberration-Flackern auf wichtigen Überschriften">
        <h2 className="fx-glitch font-display text-5xl uppercase text-primary">Shibuya Store</h2>
      </Block>

      {/* 5 Kanji rain */}
      <Block n={5} title="Kanji-Regen Hintergrund" sub="Sanft fallende Neon-Kanji als atmosphärische Deko">
        <div className="relative h-56 overflow-hidden rounded-xl border border-border bg-card">
          {["渋", "谷", "交", "換", "発", "売", "仲", "間", "祭"].map((k, i) => (
            <span key={i} className="fx-kanji text-3xl" style={{ left: `${8 + i * 10}%`, animationDuration: `${5 + (i % 4)}s`, animationDelay: `${i * 0.6}s` }}>{k}</span>
          ))}
          <div className="relative z-10 flex h-full items-center justify-center">
            <p className="font-display text-3xl uppercase text-foreground">Tokyo Atmosphäre</p>
          </div>
        </div>
      </Block>

      {/* 6 Halftone + angled panel */}
      <Block n={6} title="Manga-Panel + Halftone" sub="Schräg geschnittene Panels mit Screentone-Raster">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6" style={{ clipPath: "polygon(0 0, 100% 0, 100% 86%, 0 100%)" }}>
            <div className="fx-halftone pointer-events-none absolute inset-0" />
            <Layers className="relative h-5 w-5 text-primary" />
            <p className="relative mt-3 font-display text-2xl uppercase text-foreground">TCG – Karten</p>
            <p className="relative mt-1 text-sm text-muted-foreground">Mit Screentone-Raster & schrägem Panel-Schnitt.</p>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6" style={{ clipPath: "polygon(0 0, 100% 14%, 100% 100%, 0 100%)" }}>
            <div className="fx-halftone pointer-events-none absolute inset-0" />
            <Zap className="relative h-5 w-5 text-accent" />
            <p className="relative mt-3 font-display text-2xl uppercase text-foreground">Anime-Merch</p>
            <p className="relative mt-1 text-sm text-muted-foreground">Dynamischer, comichafter Look für die Karten.</p>
          </div>
        </div>
      </Block>

      <div className="px-6 py-12 text-center">
        <p className="text-sm text-muted-foreground">
          Welche Effekte willst du? Antworte z.B. mit <span className="font-bold text-foreground">„1, 3 und 6"</span> – oder <span className="font-bold text-foreground">„alle"</span>.
        </p>
      </div>
    </div>
  );
}
